import React, { useEffect, useMemo, useState } from "react";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import { authService } from "../../services";
import masterDataService from "../../services/masterDataService";
import { usePermissions } from "../../hooks/usePermissions";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";
import "./ContractorMasters.css";

const ContractorMasters = () => {
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission("Contractor.View");
  const canCreate = hasPermission("Contractor.Create");
  const canEdit = hasPermission("Contractor.Edit");
  const canDelete = hasPermission("Contractor.Delete");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const masterConfigs = useMemo(
    () => ({
      contractorAgencies: {
        label: "Contractor Agencies",
        columns: [
          { key: "agencyId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "agencyCode", label: "Code" },
          { key: "agencyName", label: "Agency Name" },
          { key: "contractorNames", label: "Contractors" },
          { key: "contactInfo", label: "Contact Info" },
          { key: "status", label: "Status" },
        ],
        fields: [
          { name: "agencyCode", label: "Agency Code", type: "text", placeholder: "e.g. AG-001" },
          {
            name: "agencyName",
            label: "Agency Name",
            type: "text",
            required: true,
            placeholder: "Enter agency name"
          },
          {
            name: "contractorNames",
            label: "Agency Contractor Name",
            type: "tag-input",
          },
          { name: "contactInfo", label: "Contact Info", type: "textarea", placeholder: "Address or contact details" },
          {
            name: "status",
            label: "Status",
            type: "select",
            required: true,
            options: ["Active", "Inactive"],
          },
        ],
        listFn: (tid) => masterDataService.getContractorAgencies(tid),
        createFn: masterDataService.createContractorAgency,
        updateFn: masterDataService.updateContractorAgency,
        deleteFn: masterDataService.deleteContractorAgency,
        idKey: "agencyId",
      },
    }),
    [],
  );

  const config = masterConfigs.contractorAgencies;

  useEffect(() => {
    if (tenantId && canView) {
      loadItems();
    } else if (!canView) {
      setError("You do not have permission to view contractor records");
      setLoading(false);
    } else {
      setError("Please login to view contractor masters");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, canView]);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await config.listFn(tenantId);
      setItems(data || []);
    } catch (err) {
      setError("Failed to load contractor masters. Please try again.");
      console.error("Contractor masters load error", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!canCreate) {
      setError("You do not have permission to create contractor records");
      return;
    }
    const initial = {};
    config.fields.forEach((f) => {
      initial[f.name] = "";
    });
    initial.status = initial.status || "Active";
    setFormData(initial);
    setEditingItem(null);
    setShowDrawer(true);
  };

  const handleEdit = (item) => {
    if (!canEdit) {
      setError("You do not have permission to edit contractor records");
      return;
    }
    setEditingItem(item);
    const next = {};
    config.fields.forEach((f) => {
      next[f.name] = item?.[f.name] ?? "";
    });
    next.status = next.status || "Active";
    setFormData(next);
    setShowDrawer(true);
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      setError("You do not have permission to delete contractor records");
      return;
    }
    try {
      setError("");
      await config.deleteFn(id);
      await loadItems();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete. Please try again.");
    }
  };

  const validate = () => {
    const missingRequired = (config.fields || [])
      .filter((f) => f.required)
      .some((f) => !String(formData?.[f.name] ?? "").trim());
    if (missingRequired) {
      setError("Please fill all required fields.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingItem && !canEdit) {
      setError("You do not have permission to edit contractor records");
      return;
    }
    if (!editingItem && !canCreate) {
      setError("You do not have permission to create contractor records");
      return;
    }

    if (!validate()) return;
    try {
      setSubmitting(true);
      setError("");
      const payload = { tenantId, ...formData };
      if (editingItem) {
        payload[config.idKey] = editingItem[config.idKey];
        await config.updateFn(editingItem[config.idKey], payload);
      } else {
        await config.createFn(payload);
      }
      await loadItems();
      setShowDrawer(false);
      setEditingItem(null);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to save. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredItems = items.filter((item) =>
    Object.values(item || {})
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return <PageLoading message="Loading contractor masters..." />;
  }

  if (!canView) {
    return (
      <div className="contractor-masters-page">
        <div className="empty-state">
          <h2>Access Denied</h2>
          <p>You do not have permission to view contractor records</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contractor-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Contractor Masters</h1>
          <p>Manage list of contractor agencies and their workers</p>
        </div>
        {canCreate && (
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus className="btn-icon" />
            Add Agency
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container kizantable">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search agencies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="table-info">
            <span className="count-badge">{filteredItems.length}</span>{" "}
            {filteredItems.length === 1 ? " Agency" : " Agencies"}
          </div>
        </div>

        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                {config.columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => (
                <tr key={item[config.idKey] || index}>
                  {config.columns.map((col) => (
                    <td key={col.key}>
                      {col.render ? (
                        col.render(item[col.key], item, index)
                      ) : Array.isArray(item[col.key]) ? (
                        <div className="tag-list" style={{ display: 'flex', alignItems: 'center' }}>
                          {item[col.key].slice(0, 2).map((tag, i) => (
                            <span key={i} className="status-pill active" style={{marginRight: '4px'}}>{tag}</span>
                          ))}
                          {item[col.key].length > 2 && (
                            <span className="count-badge" title={item[col.key].slice(2).join(", ")}>+{item[col.key].length - 2}</span>
                          )}
                        </div>
                      ) : col.key === "status" ? (
                        <span className={`status-badge ${item[col.key]?.toLowerCase()}`}>
                          {item[col.key]}
                        </span>
                      ) : (
                        item[col.key] ?? "-"
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(item)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(item)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SharedModal
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        title={editingItem ? `Edit Agency` : `New Agency`}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setShowDrawer(false)}>
              Cancel
            </button>
            <button type="submit" form="contractor-agency-form" className="btn-primary" disabled={submitting}>
              {submitting ? "Saving..." : "Save Agency"}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="contractor-agency-form" className="drawer-content" onSubmit={handleSubmit}>
            <div className="form-section">
              {config.fields.map((field) => (
                <div className="form-group" key={field.name}>
                  <label>
                    {field.label} {field.required && <span className="required">*</span>}
                  </label>
                  {field.type === "textarea" ? (
                    <textarea
                      value={formData[field.name] ?? ""}
                      onChange={(e) => setFormData(p => ({ ...p, [field.name]: e.target.value }))}
                      placeholder={field.placeholder || ""}
                      rows="4"
                    />
                  ) : field.type === "select" ? (
                    <select
                      value={formData[field.name] ?? "Active"}
                      onChange={(e) => setFormData(p => ({ ...p, [field.name]: e.target.value }))}
                    >
                      {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : field.type === "tag-input" ? (
                    <div className="tag-input-container">
                      <div className="tag-input-wrapper">
                        <input
                          type="text"
                          placeholder="Type and press Enter"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const val = e.target.value.trim();
                              if (val) {
                                const currentTags = formData[field.name] || [];
                                setFormData(p => ({ ...p, [field.name]: [...currentTags, val] }));
                                e.target.value = "";
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="tag-add-btn"
                          onClick={(e) => {
                            const input = e.currentTarget.previousElementSibling;
                            const val = input.value.trim();
                            if (val) {
                              const currentTags = formData[field.name] || [];
                              setFormData(p => ({ ...p, [field.name]: [...currentTags, val] }));
                              input.value = "";
                            }
                          }}
                        >
                          <HiOutlinePlus />
                          <span>Add</span>
                        </button>
                      </div>
                      <div className="selected-tags">
                        {(formData[field.name] || []).map((tag, idx) => (
                          <span key={idx} className="tag-item">
                            {tag}
                            <HiOutlineXMark
                              className="tag-remove"
                              onClick={() => {
                                const current = formData[field.name] || [];
                                setFormData(p => ({ ...p, [field.name]: current.filter((_, i) => i !== idx) }));
                              }}
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData[field.name] ?? ""}
                      onChange={(e) => setFormData(p => ({ ...p, [field.name]: e.target.value }))}
                      placeholder={field.placeholder || ""}
                    />
                  )}
                </div>
              ))}
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="delete-modal"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn-danger" onClick={() => handleDelete(deleteConfirm[config.idKey])}>Delete</button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete <strong>{deleteConfirm?.agencyName}</strong>?</p>
          <p className="warning-text">This will mark the agency as Inactive. This action cannot be undone.</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default ContractorMasters;
